// src/components/InstallPWA/InstallPWAGuide.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsContext } from '../../contexts/SettingsContext';
import { useTranslation } from 'react-i18next';
import walletLogo from '../../assets/wallet-logo.png';
import './InstallPWA.css';

// Import react-icons
import { 
  FiArrowLeft, 
  FiCheckCircle, 
  FiSmartphone, 
  FiMonitor,
  FiDownload
} from 'react-icons/fi';

const InstallPWAGuide = () => {
  const [platform, setPlatform] = useState('auto');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [detectedPlatform, setDetectedPlatform] = useState('');
  const navigate = useNavigate();
  const { theme } = useContext(SettingsContext);
  const { t } = useTranslation();

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Detect iOS
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setDetectedPlatform('ios');
    } 
    // Detect Android
    else if (/android/i.test(userAgent)) {
      setDetectedPlatform('android');
    }
    // Detect Chrome
    else if (/Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor)) {
      setDetectedPlatform('chrome');
    }
    // Detect Edge
    else if (/Edg/.test(userAgent)) {
      setDetectedPlatform('edge');
    }
    // Default to Chrome instructions for other desktop browsers
    else {
      setDetectedPlatform('chrome');
    }
    
    // Set initial platform selection
    setPlatform('auto');
    
    // Handle beforeinstallprompt event
    const beforeInstallPromptHandler = (e) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
    };
    
    // Add event listener
    window.addEventListener('beforeinstallprompt', beforeInstallPromptHandler);
    
    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      console.log('BitDog Wallet was installed successfully!');
      localStorage.setItem('pwa_installed', 'true');
    });
    
    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler);
    };
  }, []);

  // Handle platform selection
  useEffect(() => {
    if (platform === 'auto') {
      setPlatform(detectedPlatform);
    }
  }, [platform, detectedPlatform]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      
      // We've used the prompt, and can't use it again, discard it
      setDeferredPrompt(null);
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        localStorage.setItem('pwa_installed', 'true');
      }
    }
  };

  const renderPlatformInstructions = () => {
    switch (platform) {
      case 'ios':
        return (
          <div className="pwa-guide-steps">
            <div className="pwa-guide-step">
              <div className="pwa-step-number">1</div>
              <div className="pwa-step-content">
                <div className="pwa-step-title">{t('settings.pwa.ios.step1.title')}</div>
                <div className="pwa-step-description">
                  {t('settings.pwa.ios.step1.desc')}
                </div>
              </div>
            </div>
            
            <div className="pwa-guide-step">
              <div className="pwa-step-number">2</div>
              <div className="pwa-step-content">
                <div className="pwa-step-title">{t('settings.pwa.ios.step2.title')}</div>
                <div className="pwa-step-description">
                  {t('settings.pwa.ios.step2.desc')}
                </div>
                <img 
                  src="/assets/ios-share-icon.png" 
                  alt="iOS Share button" 
                  className="pwa-step-image"
                  onError={(e) => e.target.style.display = 'none'} 
                />
              </div>
            </div>
            
            <div className="pwa-guide-step">
              <div className="pwa-step-number">3</div>
              <div className="pwa-step-content">
                <div className="pwa-step-title">{t('settings.pwa.ios.step3.title')}</div>
                <div className="pwa-step-description">
                  {t('settings.pwa.ios.step3.desc')}
                </div>
                <img 
                  src="/assets/ios-add-to-home.png" 
                  alt="Add to Home Screen option" 
                  className="pwa-step-image"
                  onError={(e) => e.target.style.display = 'none'} 
                />
              </div>
            </div>
            
            <div className="pwa-guide-step">
              <div className="pwa-step-number">4</div>
              <div className="pwa-step-content">
                <div className="pwa-step-title">{t('settings.pwa.ios.step4.title')}</div>
                <div className="pwa-step-description">
                  {t('settings.pwa.ios.step4.desc')}
                </div>
              </div>
            </div>
            
            <div className="pwa-guide-step">
              <div className="pwa-step-number">5</div>
              <div className="pwa-step-content">
                <div className="pwa-step-title">{t('settings.pwa.ios.step5.title')}</div>
                <div className="pwa-step-description">
                  {t('settings.pwa.ios.step5.desc')}
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'android':
        return (
          <div className="pwa-guide-steps">
            <div className="pwa-guide-step">
              <div className="pwa-step-number">1</div>
              <div className="pwa-step-content">
                <div className="pwa-step-title">{t('settings.pwa.android.step1.title')}</div>
                <div className="pwa-step-description">
                  {t('settings.pwa.android.step1.desc')}
                </div>
              </div>
            </div>
            
            {deferredPrompt ? (
              <div className="pwa-guide-step">
                <div className="pwa-step-number">2</div>
                <div className="pwa-step-content">
                  <div className="pwa-step-title">{t('settings.pwa.android.prompt.title')}</div>
                  <div className="pwa-step-description">
                    {t('settings.pwa.android.prompt.desc')}
                  </div>
                  <button 
                    className="settings-button primary" 
                    style={{ marginTop: '10px' }} 
                    onClick={handleInstallClick}
                  >
                    <FiDownload className="button-icon" />
                    {t('settings.pwa.installButton')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="pwa-guide-step">
                  <div className="pwa-step-number">2</div>
                  <div className="pwa-step-content">
                    <div className="pwa-step-title">{t('settings.pwa.android.step2.title')}</div>
                    <div className="pwa-step-description">
                      {t('settings.pwa.android.step2.desc')}
                    </div>
                    <img 
                      src="/assets/android-more-options.png" 
                      alt="Android menu button" 
                      className="pwa-step-image"
                      onError={(e) => e.target.style.display = 'none'} 
                    />
                  </div>
                </div>
                
                <div className="pwa-guide-step">
                  <div className="pwa-step-number">3</div>
                  <div className="pwa-step-content">
                    <div className="pwa-step-title">{t('settings.pwa.android.step3.title')}</div>
                    <div className="pwa-step-description">
                      {t('settings.pwa.android.step3.desc')}
                    </div>
                    <img 
                      src="/assets/android-install-prompt.png" 
                      alt="Add to Home screen option" 
                      className="pwa-step-image"
                      onError={(e) => e.target.style.display = 'none'} 
                    />
                  </div>
                </div>
                
                <div className="pwa-guide-step">
                  <div className="pwa-step-number">4</div>
                  <div className="pwa-step-content">
                    <div className="pwa-step-title">{t('settings.pwa.android.step4.title')}</div>
                    <div className="pwa-step-description">
                      {t('settings.pwa.android.step4.desc')}
                    </div>
                  </div>
                </div>
              </>
            )}
            
            <div className="pwa-guide-step">
              <div className="pwa-step-number">{deferredPrompt ? 3 : 5}</div>
              <div className="pwa-step-content">
                <div className="pwa-step-title">{t('settings.pwa.android.finalStep.title')}</div>
                <div className="pwa-step-description">
                  {t('settings.pwa.android.finalStep.desc')}
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'chrome':
      case 'edge':
        const isEdge = platform === 'edge';
        const browserType = isEdge ? 'edge' : 'chrome';
        
        return (
          <div className="pwa-guide-steps">
            {deferredPrompt ? (
              <div className="pwa-guide-step">
                <div className="pwa-step-number">1</div>
                <div className="pwa-step-content">
                  <div className="pwa-step-title">{t('settings.pwa.desktop.prompt.title')}</div>
                  <div className="pwa-step-description">
                    {t('settings.pwa.desktop.prompt.desc')}
                  </div>
                  <button 
                    className="settings-button primary" 
                    style={{ marginTop: '10px' }} 
                    onClick={handleInstallClick}
                  >
                    <FiDownload className="button-icon" />
                    {t('settings.pwa.installButton')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="pwa-guide-step">
                  <div className="pwa-step-number">1</div>
                  <div className="pwa-step-content">
                    <div className="pwa-step-title">
                      {t(`settings.pwa.${browserType}.step1.title`)}
                    </div>
                    <div className="pwa-step-description">
                      {t(`settings.pwa.${browserType}.step1.desc`)}
                    </div>
                    <img 
                      src={isEdge ? "/assets/edge-install-icon.png" : "/assets/chrome-install-icon.png"} 
                      alt={`${isEdge ? 'Edge' : 'Chrome'} install icon`} 
                      className="pwa-step-image"
                      onError={(e) => e.target.style.display = 'none'} 
                    />
                  </div>
                </div>
                
                <div className="pwa-guide-step">
                  <div className="pwa-step-number">2</div>
                  <div className="pwa-step-content">
                    <div className="pwa-step-title">
                      {t(`settings.pwa.${browserType}.step2.title`)}
                    </div>
                    <div className="pwa-step-description">
                      {t(`settings.pwa.${browserType}.step2.desc`)}
                    </div>
                  </div>
                </div>
                
                <div className="pwa-guide-step">
                  <div className="pwa-step-number">3</div>
                  <div className="pwa-step-content">
                    <div className="pwa-step-title">
                      {t(`settings.pwa.${browserType}.step3.title`)}
                    </div>
                    <div className="pwa-step-description">
                      {t(`settings.pwa.${browserType}.step3.desc`)}
                    </div>
                  </div>
                </div>
              </>
            )}
            
            <div className="pwa-guide-step">
              <div className="pwa-step-number">{deferredPrompt ? 2 : 4}</div>
              <div className="pwa-step-content">
                <div className="pwa-step-title">
                  {t(`settings.pwa.${browserType}.finalStep.title`)}
                </div>
                <div className="pwa-step-description">
                  {t(`settings.pwa.${browserType}.finalStep.desc`)}
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={`settings-container pwa-guide-container ${theme}`}>
      <div className="pwa-guide-header">
        <img src={walletLogo} alt="BitDog Wallet" className="pwa-guide-logo" />
        <h2>{t('settings.pwa.guideTitle')}</h2>
        <p>{t('settings.pwa.guideSubtitle')}</p>
      </div>
      
      <div className="pwa-platform-selector">
        <button 
          className={`settings-button ${platform === 'ios' ? 'active' : ''}`}
          onClick={() => setPlatform('ios')}
        >
          iOS
        </button>
        <button 
          className={`settings-button ${platform === 'android' ? 'active' : ''}`}
          onClick={() => setPlatform('android')}
        >
          Android
        </button>
        <button 
          className={`settings-button ${platform === 'chrome' ? 'active' : ''}`}
          onClick={() => setPlatform('chrome')}
        >
          Chrome
        </button>
        <button 
          className={`settings-button ${platform === 'edge' ? 'active' : ''}`}
          onClick={() => setPlatform('edge')}
        >
          Edge
        </button>
      </div>
      
      {renderPlatformInstructions()}
      
      <div className="pwa-guide-footer">
        <p>{t('settings.pwa.guideFooter')}</p>
        <button 
          className="settings-button secondary" 
          onClick={() => navigate('/settings')}
          style={{ margin: '20px auto 0' }}
        >
          <FiArrowLeft className="button-icon" />
          {t('settings.pwa.backToSettings')}
        </button>
      </div>
    </div>
  );
};

export default InstallPWAGuide;